import { useState } from 'react';
import { AlertTriangle, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { poDraftImportApi, type PoVariant } from '@/lib/po-draft-import-api';
import { money, priceDiff, qtyValid, type DraftLine } from '@/lib/po-draft-utils';
import { useSearch } from './use-search';

interface Props {
  line: DraftLine; index: number; currency: string; storeId: string; token: string; disabled: boolean;
  onChange: (l: DraftLine) => void; onRemove: () => void; savedVariant?: PoVariant | null;
}

const vLabel = (v: PoVariant) => `${v.productTitle}${v.title && v.title !== 'Default Title' ? ` / ${v.title}` : ''}`;

function VariantRow({ v, currency, onPick, disabled, saved }: { v: PoVariant; currency: string; onPick: () => void; disabled: boolean; saved?: boolean }) {
  return (
    <button type="button" disabled={disabled} onClick={onPick} className="w-full text-left px-2.5 py-1.5 rounded border border-border hover:bg-muted text-[13px] flex gap-2" data-testid={`button-variant-${v.id.split('/').pop()}`}>
      <span className="flex-1 min-w-0"><span className="block truncate">{saved && <span className="mr-1.5 rounded bg-primary/15 text-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase" data-testid="badge-saved-variant">Saved match</span>}{vLabel(v)}</span>
        <span className="block text-xs text-muted-foreground font-mono">{v.sku || 'no SKU'} · {v.productStatus}</span></span>
      <span className="font-mono text-xs">{money(Number(v.price), currency)}</span>
    </button>
  );
}

export function LineEditor({ line, index, currency, storeId, token, disabled, onChange, onRemove, savedVariant }: Props) {
  const [q, setQ] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const { results, loading, error } = useSearch<PoVariant>((s, sig) => poDraftImportApi.variants(storeId, s, token, sig), q, storeId);
  const diff = priceDiff(line);
  const set = (p: Partial<DraftLine>) => onChange({ ...line, ...p });
  const pick = (v: PoVariant) => { set({ variant: v }); setQ(''); };
  const setAttr = (i: number, p: Partial<{ key: string; value: string }>) => set({ attributes: line.attributes.map((a, j) => (j === i ? { ...a, ...p } : a)) });
  const showPicker = !line.variant;
  const sid = line.savedVariantId;
  const savedRow = sid ? (line.candidates.find((c) => c.id === sid) ?? (savedVariant?.id === sid ? savedVariant : null)) : null;
  const others = line.candidates.filter((c) => c.id !== sid);
  const st = line.savedStatus;
  return (
    <div className={`glass-panel p-4 space-y-3 ${!line.variant ? 'border-amber-400' : ''}`} data-testid={`card-line-${index}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 text-[13px]">
          <div className="text-xs text-muted-foreground">Line {index + 1}{line.manual ? ' · added manually' : ' · from PDF'}</div>
          {!line.manual && <div className="font-medium truncate">{line.title || 'Untitled line'}</div>}
          {!line.manual && <div className="text-xs font-mono text-muted-foreground">SKU {line.sku || 'none'}{line.pdfPrice != null ? ` · PDF ${money(line.pdfPrice, currency)}` : ''}</div>}
        </div>
        <label className="w-20 space-y-0.5"><span className="text-[11px] text-muted-foreground">Qty</span>
          <Input inputMode="numeric" value={line.quantity} disabled={disabled} aria-invalid={!qtyValid(line)} className={!qtyValid(line) ? 'border-destructive' : ''}
            onChange={(e) => set({ quantity: e.target.value.replace(/[^\d]/g, '') })} data-testid={`input-qty-${index}`} /></label>
        {confirmRemove ? (
          <div className="flex gap-1 items-center text-xs">
            <Button size="sm" variant="destructive" disabled={disabled} onClick={onRemove} data-testid={`button-confirm-remove-${index}`}>Remove line</Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmRemove(false)}>Keep</Button>
          </div>
        ) : (
          <Button size="icon" variant="ghost" disabled={disabled} onClick={() => setConfirmRemove(true)} aria-label="Remove line" data-testid={`button-remove-${index}`}><Trash2 className="w-4 h-4" /></Button>
        )}
      </div>

      {line.variant ? (
        <div className="rounded bg-muted/50 px-3 py-2 flex items-center gap-2 text-[13px]" data-testid={`text-variant-${index}`}>
          <div className="flex-1 min-w-0"><div className="truncate font-medium">{vLabel(line.variant)}</div>
            <div className="text-xs font-mono text-muted-foreground">{line.variant.sku || 'no SKU'} · current {money(Number(line.variant.price), currency)}</div></div>
          <Button size="sm" variant="outline" disabled={disabled} onClick={() => set({ variant: null })} data-testid={`button-change-variant-${index}`}>Change</Button>
        </div>
      ) : (
        <div className="text-xs text-amber-600 flex items-center gap-1" data-testid={`text-needs-variant-${index}`}>
          <AlertTriangle className="w-3.5 h-3.5" />{line.candidates.length ? (line.exactSku ? 'Several variants share this SKU; choose one.' : 'No exact SKU match; choose a variant.') : 'No match found; search to choose a variant.'}
        </div>
      )}
      {diff && (
        <div className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1" data-testid={`text-price-diff-${index}`}>
          PDF price {money(diff.pdf, currency)} differs from current Shopify price {money(diff.current, currency)}. The draft uses the Shopify price.
        </div>
      )}
      {showPicker && st && (
        <div className="text-xs rounded border border-primary/30 bg-primary/5 px-2 py-1.5" data-testid={`text-saved-notice-${index}`}>
          {st === 'available' && 'A saved match exists for this SKU and buyer. It is a suggestion only; pick it to use it.'}
          {st === 'unavailable' && 'The saved variant for this SKU is no longer available in Shopify. Choose another variant.'}
          {st === 'ambiguous' && 'The saved match conflicts with another match for this SKU. Choose the right variant.'}
        </div>
      )}
      {showPicker && (
        <div className="space-y-2">
          {savedRow && st !== 'unavailable' && <VariantRow v={savedRow} saved currency={currency} disabled={disabled} onPick={() => pick(savedRow)} />}
          {others.map((v) => <VariantRow key={v.id} v={v} currency={currency} disabled={disabled} onPick={() => pick(v)} />)}
          <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
            <Input className="pl-8" placeholder="Search variants by SKU or title" value={q} disabled={disabled} onChange={(e) => setQ(e.target.value)} data-testid={`input-variant-search-${index}`} /></div>
          {loading && <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Searching…</div>}
          {error && <div className="text-xs text-destructive">{error}</div>}
          {!loading && !error && q.trim().length >= 2 && results.length === 0 && <div className="text-xs text-muted-foreground">No variants found.</div>}
          <div className="space-y-1 max-h-52 overflow-y-auto">{results.map((v) => <VariantRow key={v.id} v={v} currency={currency} disabled={disabled} onPick={() => pick(v)} />)}</div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">Line attributes (e.g. personalization)</span>
          <Button size="sm" variant="ghost" disabled={disabled || line.attributes.length >= 30} onClick={() => set({ attributes: [...line.attributes, { key: '', value: '' }] })} data-testid={`button-add-attr-${index}`}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button></div>
        {line.attributes.map((a, i) => (
          <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2">
            <Input className="w-full sm:w-40" placeholder="Name" maxLength={255} value={a.key} disabled={disabled} onChange={(e) => setAttr(i, { key: e.target.value })} data-testid={`input-attr-key-${index}-${i}`} />
            <Input className="min-w-0 flex-1" placeholder="Value" maxLength={1000} value={a.value} disabled={disabled} onChange={(e) => setAttr(i, { value: e.target.value })} data-testid={`input-attr-value-${index}-${i}`} />
            <Button size="icon" variant="ghost" disabled={disabled} aria-label="Remove attribute" onClick={() => set({ attributes: line.attributes.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
